import { useMemo } from 'react';
import { RoleConfig } from '../../../../../../types';
import { useUserSettings } from '../../../../../utils/use-user-settings';

export function getPermissionsFactory<
  T extends Record<string, RoleConfig[string]>,
>(roleConfig: T) {
  type Key = keyof typeof roleConfig;
  type PermissionKey = T[Key]['value'];
  const permissions = {} as { [K in PermissionKey]: boolean };
  const allPermissionsGranted = {} as { [K in PermissionKey]: boolean };

  Object.keys(roleConfig).forEach((key: string) => {
    const permissioKey = roleConfig[key as Key].value;
    permissions[permissioKey as PermissionKey] = false;
    allPermissionsGranted[permissioKey as PermissionKey] = true;
  });

  const permissonsFactory = () => ({
    permissions: { ...permissions },
    allPermissionsGranted: { ...allPermissionsGranted },
  });

  return permissonsFactory;
}

export function usePermissions<T extends Record<string, RoleConfig[string]>>(
  permissonsFactory: ReturnType<typeof getPermissionsFactory<T>>,
  settingsKey: keyof ReturnType<typeof useUserSettings>
) {
  type Permissions = ReturnType<typeof permissonsFactory>['permissions'];
  type PermissionKey = keyof Permissions;

  const settings = useUserSettings();
  const roles = settings[settingsKey] as string[];

  const permissions = useMemo(() => {
    let { permissions } = permissonsFactory();
    const { allPermissionsGranted } = permissonsFactory();

    if (roles.length === 1 && roles[0] === '*') {
      permissions = allPermissionsGranted;
    } else {
      roles.forEach((role) => {
        permissions[role as PermissionKey] = true;
      });
    }

    return { PERMISSIONS: permissions };
  }, [roles]);

  return Object.freeze(permissions);
}
